import React from "react";
import { TextField } from "@mui/material";
import {
  describePhoneList,
  detectListSeparator,
  parsePhoneList,
  splitPhoneList,
} from "../utils/phoneE164";
import { usePhoneValidationStore } from "../utils/phoneValidationStore";

/**
 * Comma-separated SMS recipient field with E.164 validation (ENG-2302).
 *
 * - Shows each invalid entry inline and reports invalidity to
 *   usePhoneValidationStore (keyed by `fieldKey`) so Save can disable.
 * - Shows the normalized numbers back ("Will text +1 225 465 9357").
 * - Calls `onValueChange` with the normalized list, joined with the same
 *   separator the stored value already uses.
 * - On open, a stored value that isn't already E.164 is flagged; when it
 *   normalizes cleanly the fix is staged via `onValueChange` so Save stores it.
 */
export const PhoneListTextField = ({
  fieldKey,
  storedValue,
  onValueChange,
  width = 300,
}: {
  fieldKey: string;
  storedValue: string | null | undefined;
  onValueChange: (value: string) => void;
  width?: number;
}) => {
  const initial = storedValue ?? "";
  const separator = React.useMemo(() => detectListSeparator(initial), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [text, setText] = React.useState<string>(initial);
  const setFieldInvalid = usePhoneValidationStore(
    (state) => state.setFieldInvalid
  );

  const list = React.useMemo(() => parsePhoneList(text), [text]);
  const serialized = list.valid.join(separator);

  const storedNeedsFix = React.useMemo(() => {
    const storedEntries = splitPhoneList(initial);
    const storedList = parsePhoneList(initial);
    return (
      storedEntries.length > 0 &&
      (storedList.hasInvalid ||
        storedList.valid.join(separator) !== storedEntries.join(separator))
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setFieldInvalid(fieldKey, list.hasInvalid);
  }, [fieldKey, list.hasInvalid, setFieldInvalid]);

  React.useEffect(() => () => setFieldInvalid(fieldKey, false), [fieldKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stage the normalized form of a stored-but-not-E.164 value so Save fixes it.
  React.useEffect(() => {
    if (storedNeedsFix && !list.hasInvalid) onValueChange(serialized);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const unchangedSinceOpen = text === initial;

  return (
    <TextField
      value={text}
      variant="standard"
      type="text"
      sx={{ width }}
      error={list.hasInvalid}
      inputProps={{ "aria-label": "Cell numbers" }}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        // Capture eagerly (see CLAUDE.md: event.target.value inside updaters).
        const nextValue = event.target.value;
        setText(nextValue);
        onValueChange(parsePhoneList(nextValue).valid.join(separator));
      }}
      helperText={
        <span data-testid={`${fieldKey}-helper`}>
          {unchangedSinceOpen && storedNeedsFix && (
            <span className="block text-[#B45309]">
              {list.hasInvalid
                ? "Saved numbers include entries SMS can't deliver to. Fix them before saving."
                : `Saved as "${initial}", which SMS would misdial. Save to store the corrected numbers.`}
            </span>
          )}
          {list.invalid.map(({ raw, result }, index) => (
            <span key={`${index}-${raw}`} className="block">
              {`"${raw}": ${result.ok ? "" : result.reason}`}
            </span>
          ))}
          {list.valid.length > 0 && (
            <span className="block">{describePhoneList(list)}</span>
          )}
          {list.entries.length === 0 && (
            <span className="block">
              Include the country code, e.g. +1 225 465 9357. Separate numbers
              with commas.
            </span>
          )}
        </span>
      }
    />
  );
};
